import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  SITE_NAME,
  HOME_SEO,
  HOME_OG_IMAGE,
  FAVICON_URL,
  ARTICLE_KEYWORDS,
  getSiteUrl,
  setDocumentTitle,
  setMetaName,
  setMetaProperty,
  setCanonical,
} from "@/lib/seo";
import { injectJsonLd, removeJsonLd } from "@/lib/schema";
import { normalizeLang, type Lang } from "@/i18n/languages";
import { getArticle } from "@/data/articles";

const ARTICLE_JSONLD_ID = "seo-article-jsonld";
const SITE_JSONLD_ID = "seo-site-jsonld";

export default function SeoManager() {
  const { pathname } = useLocation();

  useEffect(() => {
    const segments = pathname.split("/").filter(Boolean);
    const siteUrl = getSiteUrl();

    let lang: Lang = "en";
    let type: "home" | "article" | "notfound" = "notfound";
    let slug = "";

    if (segments.length === 0) {
      type = "home";
    } else if (segments.length === 1) {
      type = "home";
      lang = normalizeLang(segments[0]);
    } else if (segments.length >= 3 && segments[1] === "articles") {
      type = "article";
      lang = normalizeLang(segments[0]);
      slug = segments[2];
    }

    if (type === "notfound") {
      setDocumentTitle(`Page Not Found | ${SITE_NAME}`);
      setMetaName("robots", "noindex");
      removeJsonLd(ARTICLE_JSONLD_ID);
      removeJsonLd(SITE_JSONLD_ID);
      return;
    }

    setMetaName("robots", "index, follow");

    if (type === "home") {
      const seo = HOME_SEO[lang];
      const canonical = `${siteUrl}/${lang}`;

      setDocumentTitle(seo.title);
      setMetaName("description", seo.description);
      setMetaName("keywords", seo.keywords);
      setCanonical(canonical);

      setMetaProperty("og:type", "website");
      setMetaProperty("og:title", seo.title);
      setMetaProperty("og:description", seo.description);
      setMetaProperty("og:url", canonical);
      setMetaProperty("og:site_name", SITE_NAME);
      setMetaProperty("og:image", HOME_OG_IMAGE);

      setMetaName("twitter:card", "summary_large_image");
      setMetaName("twitter:title", seo.title);
      setMetaName("twitter:description", seo.description);
      setMetaName("twitter:image", HOME_OG_IMAGE);

      removeJsonLd(ARTICLE_JSONLD_ID);
      injectJsonLd(SITE_JSONLD_ID, {
        "@context": "https://schema.org",
        "@type": "WebSite",
        name: SITE_NAME,
        url: canonical,
        inLanguage: lang,
        publisher: {
          "@type": "Organization",
          name: SITE_NAME,
          url: canonical,
          logo: {
            "@type": "ImageObject",
            url: FAVICON_URL,
          },
        },
      });
      return;
    }

    const article = getArticle(lang, slug);
    if (!article) {
      setDocumentTitle(`Article Not Found | ${SITE_NAME}`);
      setMetaName("robots", "noindex");
      removeJsonLd(ARTICLE_JSONLD_ID);
      removeJsonLd(SITE_JSONLD_ID);
      return;
    }

    const canonical = `${siteUrl}/${lang}/articles/${article.slug}`;

    setDocumentTitle(article.title);
    setMetaName("description", article.metaDescription);
    setMetaName("keywords", ARTICLE_KEYWORDS);
    setCanonical(canonical);

    setMetaProperty("og:type", "article");
    setMetaProperty("og:title", article.title);
    setMetaProperty("og:description", article.metaDescription);
    setMetaProperty("og:url", canonical);
    setMetaProperty("og:site_name", SITE_NAME);
    setMetaProperty("og:image", article.image);

    setMetaName("twitter:card", "summary_large_image");
    setMetaName("twitter:title", article.title);
    setMetaName("twitter:description", article.metaDescription);
    setMetaName("twitter:image", article.image);

    removeJsonLd(SITE_JSONLD_ID);
    injectJsonLd(ARTICLE_JSONLD_ID, {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: article.h1,
      description: article.metaDescription,
      image: article.image,
      dateModified: article.updated,
      datePublished: article.updated,
      inLanguage: lang,
      mainEntityOfPage: {
        "@type": "WebPage",
        "@id": canonical,
      },
      publisher: {
        "@type": "Organization",
        name: SITE_NAME,
        logo: {
          "@type": "ImageObject",
          url: FAVICON_URL,
        },
      },
      author: {
        "@type": "Organization",
        name: SITE_NAME,
      },
    });
  }, [pathname]);

  return null;
}